import { Location, OrganizationSettings } from "@nova/schemas"
import { createSelector, createSlice } from "@reduxjs/toolkit"
import { RootState } from "integrations/redux"

type ConfigurationSlice = {
    workspaceName: string | undefined
    location: Location | undefined
    settings: OrganizationSettings | undefined
    isTakingStartingLocationSnapshot: boolean
    hasUnsavedChanges: boolean
    isSavingChanges: boolean
}

const initialState: ConfigurationSlice = {
    workspaceName: undefined,
    location: undefined,
    settings: undefined,
    isTakingStartingLocationSnapshot: false,
    hasUnsavedChanges: false,
    isSavingChanges: false,
}

export const configurationSliceName = "configuration" as const

export const {
    actions: {
        setConfigurationFromSource,
        setWorkspaceName,
        setLocation,
        setSetting,
        setIsTakingStartingLocationSnapshot,
        setIsSavingChanges,
        setHasUnsavedChanges,
    },
    reducer: ConfigurationSliceReducer,
} = createSlice({
    name: configurationSliceName,
    initialState,
    reducers: {
        setConfigurationFromSource: (
            state,
            action: {
                payload: Omit<
                    ConfigurationSlice,
                    "isSavingChanges" | "isTakingStartingLocationSnapshot"
                >
            }
        ) => {
            state.workspaceName = action.payload.workspaceName
            state.location = action.payload.location
            state.settings = action.payload.settings
            state.hasUnsavedChanges = action.payload.hasUnsavedChanges
            state.isSavingChanges = false
        },
        setWorkspaceName: (state, action: { payload: ConfigurationSlice["workspaceName"] }) => {
            state.workspaceName = action.payload
            state.hasUnsavedChanges = true
        },
        setLocation: (state, action: { payload: ConfigurationSlice["location"] }) => {
            state.location = action.payload
            state.hasUnsavedChanges = true
        },
        setSetting: (
            state,
            action: { payload: NonNullable<ConfigurationSlice["settings"]>[number] }
        ) => {
            const { name, value } = action.payload
            if (!state.settings) {
                state.settings = []
            }
            const existingIndex = state.settings?.findIndex((s) => s.name === name) ?? -1
            if (existingIndex !== -1) {
                state.settings?.splice(existingIndex, 1)
            }
            state.settings.push({ name, value })
            state.hasUnsavedChanges = true
        },
        setIsTakingStartingLocationSnapshot: (
            state,
            action: { payload: ConfigurationSlice["isTakingStartingLocationSnapshot"] }
        ) => {
            state.isTakingStartingLocationSnapshot = action.payload
        },
        setIsSavingChanges: (state, action: { payload: ConfigurationSlice["isSavingChanges"] }) => {
            state.isSavingChanges = action.payload
        },
        setHasUnsavedChanges: (
            state,
            action: { payload: ConfigurationSlice["hasUnsavedChanges"] }
        ) => {
            state.hasUnsavedChanges = action.payload
        },
    },
})

const root = (state: RootState) => state[configurationSliceName]

export const selectConfiguration = createSelector([root], (s) => ({
    workspaceName: s.workspaceName,
    location: s.location,
    settings: s.settings,
    hasUnsavedChanges: s.hasUnsavedChanges,
}))

export const selectHasUnsavedConfigurationChanges = createSelector(
    [root],
    (s) => s.hasUnsavedChanges
)
export const selectIsSavingChanges = createSelector([root], (s) => s.isSavingChanges)

export const selectWorkspaceName = createSelector([root], (s) => s.workspaceName)
export const selectLocation = createSelector([root], (s) => s.location)
export const selectSettings = createSelector([root], (s) => s.settings)
export const selectIsTakingStartingLocationSnapshot = createSelector(
    [root],
    (s) => s.isTakingStartingLocationSnapshot
)
